import React, { useEffect, useMemo, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridPaginationModel,
} from "@mui/x-data-grid";
import DeleteIcon from "@assets/componentIcons/DeleteIcon";
import DeleteUserModal from "@components/layout/admin-layout/components/deleteUserModal/DeleteUserModal";
import ChangeUserPlanModal from "@components/layout/superadmin-layout/changeUserPlanModal/ChangeUserPlanModal";
import ConfirmationModal from "@components/layout/superadmin-layout/confirmationModal/ConfirmationModal";
import updateIcon from "../../../../../assets/icons/Icon-up.svg";
import updateIconUp from "../../../../../assets/icons/Icon-down.svg";
import { useStore } from "@stores/root-store";
import { useNavigate } from "react-router-dom";
import { constRoute } from "@utils/route";
import { IconButton, Avatar, Typography, Box, Button } from "@mui/material";

interface User {
  account_holder: string;
  user_email: string;
  plan: string;
  country: string;
  date_joined: string;
  entity_type: string;
  last_logged_in: string;
  credits_used?: number;
  credits_purchased?: number;
  "Credits used / Credits purchased"?: string;
  [key: string]: any;
}

const UserTable = () => {
  // Test if component is rendering at all
  alert("UserTable component is rendering!");

  console.log("=== UserTable Component Rendered ===");
  console.warn("=== UserTable Component Rendered (WARN) ===");
  console.error("=== UserTable Component Rendered (ERROR) ===");

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [userEmailToDelete, setUserEmailToDelete] = useState<string | null>(
    null,
  );
  const [userToUpdate, setUserToUpdate] = useState<string | null>(null);
  const [typeToUpdate, setTypeToUpdate] = useState("Basic");
  const [openUpdatePlanModal, setOpenUpdatePlanModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [currentUserType, setCurrentUserType] = useState("");
  const [isUpgrade, setIsUpgrade] = useState<boolean | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const navigate = useNavigate();
  const {
    user: { getAllUsers: allUsers, onDeleteUser, setAllUsers },
  } = useStore(null);

  console.log("Store data:", allUsers);
  console.warn("Store data (WARN):", allUsers);

  const usersForSelect = useMemo(() => {
    return (
      allUsers["account holders"]?.map((el) => ({
        label: `${el.account_holder} - ${el.user_email}`,
        value: `${el.account_holder} - ${el.user_email}`,
      })) || []
    );
  }, [allUsers]);

  const handleUserDelete = async (email: string) => {
    const updatedUsers =
      allUsers["account holders"]?.filter((el) => el.user_email !== email) ||
      [];

    onDeleteUser(email);
    setAllUsers({ ...allUsers, ["account holders"]: updatedUsers });
  };

  useEffect(() => {
    const email = userToUpdate?.split("-")[1]?.trim();
    const user = allUsers["account holders"]?.find(
      (el) => el.user_email === email,
    );
    if (user) setCurrentUserType(user.plan);
  }, [userToUpdate, allUsers]);

  // Reset all modals
  const resetAllModals = () => {
    console.log("Resetting all modals");
    setIsOpenModal(false);
    setOpenUpdatePlanModal(false);
    setOpenConfirmationModal(false);
    setUserEmailToDelete(null);
    setUserToUpdate(null);
    setIsUpgrade(null);
  };

  // Handle upgrade action
  const handleUpgrade = (e: React.MouseEvent, params: GridRenderCellParams) => {
    e.preventDefault();
    e.stopPropagation();

    alert("Upgrade clicked!");
    console.log("=== UPGRADE CLICKED ===");
    console.log("User email:", params.row.user_email);
    console.log("User plan:", params.row.plan);
    console.log("Account holder:", params.row.account_holder);

    // Reset all modals first
    resetAllModals();

    // Set upgrade specific states
    setCurrentUserType(params.row.plan);
    setIsUpgrade(true);
    setUserToUpdate(`${params.row.account_holder} - ${params.row.user_email}`);
    setOpenUpdatePlanModal(true);

    console.log("States set - isUpgrade:", true, "openUpdatePlanModal:", true);
  };

  // Handle downgrade action
  const handleDowngrade = (
    e: React.MouseEvent,
    params: GridRenderCellParams,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    alert("Downgrade clicked!");
    console.log("=== DOWNGRADE CLICKED ===");
    console.log("User email:", params.row.user_email);
    console.log("User plan:", params.row.plan);
    console.log("Account holder:", params.row.account_holder);

    // Reset all modals first
    resetAllModals();

    // Set downgrade specific states
    setCurrentUserType(params.row.plan);
    setIsUpgrade(false);
    setUserToUpdate(`${params.row.account_holder} - ${params.row.user_email}`);
    setOpenUpdatePlanModal(true);

    console.log("States set - isUpgrade:", false, "openUpdatePlanModal:", true);
  };

  // Handle delete action
  const handleDelete = (e: React.MouseEvent, params: GridRenderCellParams) => {
    e.preventDefault();
    e.stopPropagation();

    alert("Delete clicked!");
    console.log("=== DELETE CLICKED ===");
    console.log("User email:", params.row.user_email);
    console.log("Account holder:", params.row.account_holder);

    // Reset all modals first
    resetAllModals();

    // Set delete specific states
    setUserEmailToDelete(params.row.user_email);
    setIsOpenModal(true);

    console.log(
      "States set - userEmailToDelete:",
      params.row.user_email,
      "isOpenModal:",
      true,
    );
  };

  // Test button handlers
  const testUpgrade = () => {
    alert("Test Upgrade clicked!");
    console.log("=== TEST UPGRADE BUTTON CLICKED ===");
    setIsUpgrade(true);
    setUserToUpdate("Test User - test@example.com");
    setOpenUpdatePlanModal(true);
  };

  const testDelete = () => {
    alert("Test Delete clicked!");
    console.log("=== TEST DELETE BUTTON CLICKED ===");
    setUserEmailToDelete("test@example.com");
    setIsOpenModal(true);
  };

  const columns: GridColDef[] = [
    {
      field: "account_holder",
      headerName: "Name",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            cursor: "pointer",
            py: 0.5,
          }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `${constRoute.masteradminUsers}?email=${encodeURIComponent(
                params.row.user_email,
              )}`,
            );
          }}
        >
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              color: "#000",
              borderBottom: "1px solid #000",
              display: "inline-block",
              lineHeight: 1.2,
              padding: 0,
              margin: 0,
              width: "fit-content",
            }}
          >
            {params.row.account_holder}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.8rem" }}
          >
            {params.row.user_email}
          </Typography>
        </Box>
      ),
    },
    {
      field: "plan",
      headerName: "Plan",
      width: 120,
      flex: 1,
    },
    {
      field: "entity_type",
      headerName: "Entity Type",
      width: 150,
      flex: 1,
    },
    {
      field: "credits",
      headerName: "Credits used / Credits purchased",
      flex: 1,
    },
    {
      field: "last_logged_in",
      headerName: "Last Login",
      width: 150,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            height: "100%",
          }}
        >
          {/* Upgrade Icon - Based on your working code, updateIconUp is for upgrade */}
          <IconButton
            size="small"
            onClick={(e) => handleUpgrade(e, params)}
            sx={{
              padding: "4px",
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
            }}
            title="Upgrade plan"
            data-testid="upgrade-button"
          >
            <img
              src={updateIconUp}
              alt="upgrade plan"
              style={{
                width: "16px",
                height: "16px",
                display: "block",
                pointerEvents: "none",
              }}
            />
          </IconButton>

          {/* Downgrade Icon - Based on your working code, updateIcon is for downgrade */}
          <IconButton
            size="small"
            onClick={(e) => handleDowngrade(e, params)}
            sx={{
              padding: "4px",
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "rgba(255, 152, 0, 0.04)",
              },
            }}
            title="Downgrade plan"
            data-testid="downgrade-button"
          >
            <img
              src={updateIcon}
              alt="downgrade plan"
              style={{
                width: "16px",
                height: "16px",
                display: "block",
                pointerEvents: "none",
              }}
            />
          </IconButton>

          {/* Delete Icon */}
          <IconButton
            size="small"
            onClick={(e) => handleDelete(e, params)}
            sx={{
              padding: "4px",
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "rgba(244, 67, 54, 0.04)",
              },
            }}
            title="Delete user"
            data-testid="delete-button"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const rows = useMemo(() => {
    const mappedRows =
      allUsers?.["account holders"]?.map((el: User, index) => ({
        id: index,
        ...el,
        credits:
          el["Credits used / Credits purchased"] ||
          `${el.credits_used ?? 0} / ${el.credits_purchased ?? 0}`,
      })) || [];

    console.log("Mapped rows:", mappedRows);
    console.warn("Mapped rows (WARN):", mappedRows);
    return mappedRows;
  }, [allUsers]);

  // Debug pagination changes
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    alert("Pagination changed!");
    console.log("Pagination changed:", newModel);
    setPaginationModel(newModel);
  };

  console.log("About to render DataGrid with rows:", rows.length);
  console.warn("About to render DataGrid with rows (WARN):", rows.length);

  return (
    <>
      {/* Test buttons outside DataGrid */}
      <div
        style={{
          marginBottom: "20px",
          padding: "10px",
          backgroundColor: "#e3f2fd",
        }}
      >
        <h4>Test Buttons (Outside DataGrid):</h4>
        <Button
          onClick={testUpgrade}
          variant="contained"
          color="primary"
          style={{ marginRight: "10px" }}
        >
          Test Upgrade
        </Button>
        <Button onClick={testDelete} variant="contained" color="error">
          Test Delete
        </Button>
      </div>

      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
        disableColumnResize
        pageSizeOptions={[5, 10, 20, 50]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pagination
        paginationMode="client"
        sx={{
          borderColor: "#E6E6E6",
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F7F7F7",
            fontSize: "0.875rem",
            fontWeight: 600,
          },
          "& .MuiDataGrid-cell": {
            border: "none",
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.02)",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid #E6E6E6",
            backgroundColor: "#F7F7F7",
          },
          "& .MuiTablePagination-root": {
            fontSize: "0.875rem",
          },
          "& .MuiTablePagination-selectLabel": {
            fontSize: "0.875rem",
          },
          "& .MuiTablePagination-displayedRows": {
            fontSize: "0.875rem",
          },
        }}
      />

      {/* Debug info */}
      <div
        style={{
          marginTop: "20px",
          padding: "10px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <h4>Debug Info:</h4>
        <p>
          Current pagination: Page {paginationModel.page + 1}, Size:{" "}
          {paginationModel.pageSize}
        </p>
        <p>Total rows: {rows.length}</p>
        <p>Delete modal open: {isOpenModal.toString()}</p>
        <p>Update plan modal open: {openUpdatePlanModal.toString()}</p>
        <p>User to delete: {userEmailToDelete || "None"}</p>
        <p>User to update: {userToUpdate || "None"}</p>
        <p>Is upgrade: {isUpgrade?.toString() || "None"}</p>
        <p>
          All users data:{" "}
          {JSON.stringify(allUsers ? Object.keys(allUsers) : "No data")}
        </p>
      </div>

      {/* Change User Plan Modal */}
      <ChangeUserPlanModal
        isOpen={openUpdatePlanModal}
        users={usersForSelect}
        userToUpdate={userToUpdate}
        typeToUpdate={typeToUpdate}
        closeModal={() => {
          setOpenUpdatePlanModal(false);
          setUserToUpdate(null);
          setIsUpgrade(null);
        }}
        setUserToUpdate={setUserToUpdate}
        setTypeToUpdate={setTypeToUpdate}
        currentUserType={currentUserType}
        isUpgrade={isUpgrade}
        setOpenConfirmationModal={setOpenConfirmationModal}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={openConfirmationModal}
        closeModal={() => {
          setOpenConfirmationModal(false);
          setUserToUpdate(null);
          setIsUpgrade(null);
        }}
        userToUpdate={userToUpdate}
        typeToUpdate={typeToUpdate}
        isUpgrade={isUpgrade}
      />

      {/* Delete User Modal */}
      <DeleteUserModal
        onDeleteRecord={() => {
          setIsOpenModal(false);
          if (userEmailToDelete) {
            handleUserDelete(userEmailToDelete);
          }
          setUserEmailToDelete(null);
        }}
        isOpen={isOpenModal}
        username={
          allUsers["account holders"]?.find(
            (el) => el.user_email === userEmailToDelete,
          )?.account_holder || ""
        }
        closeModal={() => {
          setIsOpenModal(false);
          setUserEmailToDelete(null);
        }}
      />
    </>
  );
};

export default UserTable;
